// src/components/Header.tsx

import React from "react";
import { Link, useLocation } from "react-router-dom";
import TabDropdownList from "./TabDropdownList";
import { useHamburgerMenu } from "../contexts/HamburgerMenuContext";
import SearchButton from "./SearchButton";
import CartButton from "./CartButton";
import { useSearch } from "../contexts/SearchContext";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import SeasonalRibbon from "./SeasonalRibbon";
import WishlistHeaderButton from "./WishlistHeaderButton";

const Header: React.FC = () => {
  const { isHamburgerMenuOpen, setIsHamburgerMenuOpen } = useHamburgerMenu();
  const { isSearchMenuOpen, setIsSearchMenuOpen } = useSearch();
  const { isLoggedIn, session } = useCustomerAuth();
  const location = useLocation();
  const isHomePage = location.pathname === "/" || location.pathname === "/home";

  const handleOnSearchClick = () => {
    setIsSearchMenuOpen(!isSearchMenuOpen);
  };

  return (
    <header className="header">
      {/* min-w-0 lets this group actually shrink. Without it the group keeps
          its content width on a narrow screen and the wordmark runs straight
          under the search button. */}
      <div className="flex gap-3 sm:gap-4 items-center min-w-0">
        {/* <!-- Hamburger Menu --> */}
        <button
          id="menuButton"
          className="md:hidden lg:hidden xl:hidden 2xl:hidden"
          aria-label="Open navigation menu"
          aria-expanded={isHamburgerMenuOpen}
          onClick={() => setIsHamburgerMenuOpen(true)}
        >
          <svg
            className="w-8 h-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 6h16M4 12h16m-7 6h7"
            />
          </svg>
        </button>

        {/* Logo */}
        <Link
          to="/home"
          className="header-logo flex gap-2 sm:gap-3 justify-center items-center min-w-0"
        >
          {/* Bison mark hidden for now (owner request, 2026-09-06). Kept in
              place rather than deleted so it can be restored in one line; the
              <360px rule in App.css that dropped the wordmark in its favour is
              disabled to match, otherwise the header would carry no brand at
              all on the narrowest phones. */}
          {/*
          <img
            src="/images/logo-bison.png"
            alt=""
            aria-hidden="true"
            className="header-logo-mark"
          />
          */}
          <img
            src="/images/logo-title-main-name-sm.png"
            alt="Americana &amp; Company"
            className="header-logo-foreground"
          />
        </Link>

        {/* Navigation Tabs */}
        <nav className="hidden md:flex lg:flex xl:flex 2xl:flex gap-3 px-12">
          <TabDropdownList />
        </nav>
      </div>

      {/* Search, Wishlist, Account, and Cart Buttons */}
      {/* Tighter gaps on mobile buy width for the wordmark. This changes only
          the space BETWEEN the buttons — each button keeps its full size, so
          tap targets are unchanged. */}
      <div className="flex flex-row gap-1 sm:gap-2 items-center">
        <SearchButton onClick={handleOnSearchClick} />
        <WishlistHeaderButton />
        <CartButton />
        <Link
          to={isLoggedIn ? "/account" : "/account/login"}
          aria-label={isLoggedIn ? "My Account" : "Sign In"}
          className="flex items-center justify-center p-1.5 mb-1 transition duration-200 relative"
          title={isLoggedIn ? `Signed in as ${session?.email}` : "Sign In"}
        >
          {/* Person silhouette icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-5 w-5"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          {/* Green dot when logged in */}
          {isLoggedIn && (
            <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full border border-white" />
          )}
        </Link>
      </div>
      {isHomePage && <SeasonalRibbon />}
    </header>
  );
};

export default Header;
